import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import Icon from "./Icon";
import { useResizeContext } from "./ResizeablePanel";
import PropTypes from "prop-types";
import { withComponentCapabilities } from "./capabilities/componentCapabilities";

/**
 * Provides a vertical scrolling carousel control to display entries in a vertical orientation.
 * @component
 */
const VerticalCarousel = withComponentCapabilities(({ items, interval = 0, showPause = false, showControls = false }) => {
    const containerRef = useRef(null);
    const { height } = useResizeContext();
    const [paused, setPaused] = useState(false);
    const [index, setIndex] = useState(0);

    const scrollToIndex = useCallback((newIndex) => {
        const clamped = Math.max(0, Math.min(newIndex, items.length - 1));
        const container = containerRef.current;
        if (container) {
            container.scrollTo({ top: clamped * height, behavior: "smooth" });
        }
        setIndex(clamped);
    }, [items, height]);

    useEffect(() => {
        if (paused || items.length <= 1 || interval <= 0) return;

        const scrollInterval = setInterval(() => {
            scrollToIndex((index + 1) % items.length);
        }, interval);

        return () => clearInterval(scrollInterval);
    }, [paused, index, items.length, height, interval, scrollToIndex]);

    return <div className="h-100 w-100" style={{ position: "relative", height, overflow: "hidden" }}>
        <div ref={containerRef} style={{ display: "flex", flexDirection: "column", transition: "scroll-behavior 0.5s", scrollBehavior: "smooth", height: "100%", overflowY: "scroll", scrollbarWidth: "none", msOverflowStyle: "none", scrollSnapType: "y mandatory" }}>
            {items.map(item =>
                <div style={{ height, flexShrink: 0, scrollSnapAlign: "start" }}>
                    {item}
                </div>
            )}
            <div className={`position-absolute d-flex ${height > 120 ? 'flex-column' : 'flex-row'} align-items-end justify-content-end`} style={{ bottom: 4, right: 4, gap: '4px' }}>
                {showControls && <Button variant="outline-dark" className="position-relative" size="sm" style={{ width: '24px' }} onClick={() => scrollToIndex(index - 1)}><Icon intent="up"></Icon></Button>}
                {showPause && <Button variant="outline-dark" size="sm" className="position-relative" style={{ width: '24px' }} onClick={() => setPaused(p => !p)}><Icon variant="solid" kind={paused ? "play" : "pause"}></Icon></Button>}
                {showControls && <Button variant="outline-dark" size="sm" className="position-relative" style={{ width: '24px' }} onClick={() => scrollToIndex(index + 1)}><Icon intent="down"></Icon></Button>}
            </div>
        </div>
    </div>
}, {
    lazyProps: ['items']
});
VerticalCarousel.propTypes = {
    /**
     * An array of items to render in the carousel
     */
    items: PropTypes.array,
    /**
     * The duration a single entry stays on screen when autoscrolling - zero is no autoscroll
     */
    interval: PropTypes.number,
    /**
     * Set to true to show a pause button to stop and restart automated scrolling
     */
    showPause: PropTypes.bool,
    /**
     * Set to true to show next and previous buttons to permit navigation between carousel items
     */
    showControls: PropTypes.bool
}

export default VerticalCarousel;
